import React, { useContext, useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { TextBlock } from 'react-placeholder/lib/placeholders';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
	makeStyles,
	Card,
	CardContent,
	Grid,
	IconButton,
	Tooltip
} from '@material-ui/core';

import Constants from "../utils/Constants";
import { useEllipsis } from "../utils/CustomHooks";
import ActivityCategory from "./ActivityCategory";
import ActivityTopics from "./ActivityTopics";
import Timestamp from "./Timestamp";
import SourceIndicator from "./SourceIndicator";
import ActivityTitle from "./ActivityTitle";
import ActivityListItemContent from "./ActivityListItemContent";
import AuthorizedUser from "../utils/AuthorizedUser";
import AuthorizedUserContext from "../utils/AuthorizedUserContext";

const placeholderColor = Constants.COLORS.PLACEHOLDER_BG;
export const ItemPlaceholder = (
	<Card elevation={0} style={{ marginBottom: 16 }}>
		<CardContent>
			<div style={{ display: "flex", justifyContent: "space-between" }}>
				<TextBlock rows={1} color={placeholderColor} style={{ width: "40%" }} />
				<TextBlock rows={1} color={placeholderColor} style={{ width: "15%" }} />
			</div>
			<TextBlock rows={1} color={placeholderColor} style={{ width: "85%", marginTop: 12 }} />
			<TextBlock rows={1} color={placeholderColor} style={{ marginTop: 12 }} />
		</CardContent>
	</Card>
);

const styles = makeStyles(theme => ({
	card: {
		cursor: "pointer",
		height: theme.spacing(18.5),
		borderRadius: '15px'
	},

	cardRoot: {
		paddingTop: theme.spacing(1.625),
		paddingBottom: theme.spacing(1.75),

		"&:last-child": {
			paddingBottom: theme.spacing(1.75)
		}
	},

	selected: {
		backgroundColor: '#e7f2f4'
	},

	read: {
		opacity: 0.5
	},

	topRow: {
		marginBottom: theme.spacing(1)
	},

	bottomRow: {
		marginBottom: theme.spacing(0.5)
	},

	categoryItem: {
		paddingRight: theme.spacing(1),
		paddingTop: theme.spacing(0.5)
	},

	sourceIconItem: {
		paddingRight: theme.spacing(0.5)
	},

	actionButton: {
		fontSize: "1rem",
		color: theme.palette.primary.darkGrey
	},

	actionButtonRoot: {
		width: theme.spacing(3),
		height: theme.spacing(3)
	},

	noEllipsis: {
		"&:before": {
			content: 'no-close-quote'
		}
	}
}));

function ActivityListItem(props) {
	const contentDivRef = useRef();
	const [isOverflown, setIsOverflown] = useState(false);
	const authenticatedUser = useContext(AuthorizedUserContext);

	let classes = styles(),
		ellipsisClasses = useEllipsis(),
		{ className, category, postMatches, time, published,
			sourceId, subSourceId, sourceIcon, content,
			selected, activityId, clickHandler, actionButtons,
			priorComments, patch, pulsateAlerts,
			views, title, twitterRedacted, history, pattern, searchTerm } = props,
		read = Constants.ACTIVITY.hasViewed(authenticatedUser, views),
		patternDetected = category === Constants.ACTIVITY.CATEGORIES.DISCUSSIONS.ID && Constants.PATTERN.isDetectionDate(pattern, published);

	useEffect(() => {
		if (contentDivRef.current && !twitterRedacted) {
			let { clientHeight, scrollHeight } = contentDivRef.current;
			setIsOverflown(scrollHeight > clientHeight);
		}
	}, [contentDivRef, twitterRedacted]);

	return (
		<Card
			id={activityId}
			elevation={selected ? 4 : 1}
			onClick={() => clickHandler(activityId)}
			className={`${className} ${classes.card} ${selected ? classes.selected : read ? classes.read : ""}`}>
			<CardContent classes={{ root: classes.cardRoot }}>
				<Grid
					container
					wrap="nowrap"
					alignItems="flex-start"
					justify="space-between"
					className={classes.topRow}>
					<Grid item xs={10} sm={10} md={10} lg={10} xl={10}>
						<Grid
							container
							wrap="nowrap"
							justify="flex-start"
							alignItems="flex-start">
							<Grid item className={classes.categoryItem}>
								<ActivityCategory
									useDot
									category={category}
									pulsateAlerts={pulsateAlerts}
									colorOverride={patternDetected ? "orange" : null} />
							</Grid>
							<Grid item>
								<ActivityTopics matches={postMatches} />
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={2} sm={2} md={2} lg={2} xl={2}>
						<Grid
							container
							wrap="nowrap"
							justify="flex-end"
							alignItems="center">
							{actionButtons && actionButtons.map((btn, idx) => {
								return btn.show(props) ? (
									<Tooltip
										key={idx}
										placement="top"
										title={category === 1 ? "Alert" : "Discussion"}>
										<Grid item>
											<IconButton
												key={idx}
												//aria-label={btn.title}
												tabIndex="-1"
												aria-disabled="true"
												className={classes.actionButton}
												classes={{ root: classes.actionButtonRoot }}>
												<FontAwesomeIcon
													icon={category === 1 ? ['fas', 'bell'] : ['fas', 'comments-alt']} />
											</IconButton>
										</Grid>
									</Tooltip>
								) : null;
							})}
						</Grid>
					</Grid>
				</Grid>
				<Grid
					container
					alignItems="center"
					className={classes.bottomRow}>
					<Grid item className={classes.sourceIconItem}>
						<SourceIndicator {...sourceIcon} />
					</Grid>
					<Grid item>
						<Timestamp time={time} format="ddd MMM D YYYY" overYearFormat="MMM Do YYYY" />
					</Grid>
				</Grid>
				<div
					ref={contentDivRef}
					className={`${ellipsisClasses.ellipsis} ${isOverflown ? '' : classes.noEllipsis}`}>
					<ActivityTitle
						title={title}
						twitterRedacted={twitterRedacted} />
					<ActivityListItemContent
						patch={patch}
						content={content}
						sourceId={sourceId}
						subSourceId={subSourceId}
						priorComments={priorComments}
						category={category}
						history={history}
						twitterRedacted={twitterRedacted}
						searchTerm={searchTerm} />
				</div>
			</CardContent>
		</Card>
	);
}

ActivityListItem.defaultProps = {
	selected: false
};

ActivityListItem.propTypes = {
	history: PropTypes.object,
	className: PropTypes.string,
	category: PropTypes.number,
	postMatches: PropTypes.array,
	time: PropTypes.string,
	published: PropTypes.string,
	sourceId: PropTypes.number,
	subSourceId: PropTypes.string,
	sourceIcon: PropTypes.object,
	patch: PropTypes.string,
	title: PropTypes.string,
	twitterRedacted: PropTypes.string,
	content: PropTypes.string,
	selected: PropTypes.bool,
	clickHandler: PropTypes.func,
	activityId: PropTypes.number,
	actionButtons: PropTypes.array,
	priorComments: PropTypes.array,
	pulsateAlerts: PropTypes.bool,
	views: PropTypes.array,
	currentStatus: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	pattern: PropTypes.object,
};

export default ActivityListItem;